// Panel nápovědy: stránky uživatelské příručky z wiki podle obrazovky, na které uživatel je
// (HelpPresenter). Otevírá se ikonou v horní liště nebo F1. Odkazy mezi stránkami příručky
// se otevírají v panelu, správce může zobrazenou stránku přiřadit obrazovce.

import naja from 'naja';

// Otevřený panel zůstane otevřený i po přechodu na jinou stránku (jen v této záložce prohlížeče)
const STORAGE_KEY = 'wisart.helpPanelOpen';

const rememberOpen = (open) => {
    try {
        if (open) sessionStorage.setItem(STORAGE_KEY, '1');
        else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        // prohlížeč úložiště nepovolí - panel se jen nebude pamatovat
    }
};

const wasOpen = () => {
    try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
        return false;
    }
};

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

class HelpPanel {
    constructor(toggle, panel) {
        this.toggle = toggle;
        this.panel = panel;
        this.body = panel.querySelector('[data-help-body]');
        this.title = panel.querySelector('.help-panel-title');
        this.back = panel.querySelector('[data-help-back]');
        this.wiki = panel.querySelector('[data-help-wiki]');
        this.assign = panel.querySelector('[data-help-assign]');
        this.history = [];
        this.current = null;
        this.context = null;

        toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
        panel.querySelector('[data-help-close]').addEventListener('click', () => this.close());
        panel.querySelector('[data-help-refresh]').addEventListener('click', () => this.load(this.current?.doc ?? null, { refresh: true, push: false }));
        panel.querySelector('[data-help-contents]').addEventListener('click', () => this.load('', { push: true }));
        this.back.addEventListener('click', () => {
            const previous = this.history.pop();
            this.load(previous ?? null, { push: false });
        });
        this.body.addEventListener('click', (event) => this.handleLink(event));
        this.assign.addEventListener('submit', (event) => this.handleAssign(event));

        document.addEventListener('keydown', (event) => {
            if (event.key === 'F1') {
                event.preventDefault();
                this.isOpen() ? this.close() : this.open();
            } else if (event.key === 'Escape' && this.isOpen()) {
                this.close();
            }
        });
    }

    isOpen() {
        return this.panel.classList.contains('open');
    }

    /** @param {boolean} animate false = po načtení stránky rovnou otevřený, bez vysouvání */
    open(animate = true) {
        if (!animate) {
            this.panel.style.transition = 'none';
            requestAnimationFrame(() => requestAnimationFrame(() => { this.panel.style.transition = ''; }));
        }
        this.panel.classList.add('open');
        rememberOpen(true);
        this.panel.setAttribute('aria-hidden', 'false');
        this.toggle.setAttribute('aria-expanded', 'true');
        this.history = [];
        this.load(null, { push: false });
    }

    close() {
        this.panel.classList.remove('open');
        rememberOpen(false);
        this.panel.setAttribute('aria-hidden', 'true');
        this.toggle.setAttribute('aria-expanded', 'false');
    }

    /** Obrazovka se mohla změnit (Naja) - když je panel otevřený na nápovědě obrazovky, načte novou */
    screenChanged() {
        if (this.isOpen() && this.history.length === 0 && this.screenKey() !== this.context) {
            this.load(null, { push: false });
        }
    }

    activeTab() {
        return document.querySelector('.app-body .nav-tabs .nav-link.active')?.id ?? '';
    }

    screenKey() {
        return window.location.pathname + window.location.search + '|' + this.activeTab();
    }

    /**
     * @param {string|null} doc null = stránka podle obrazovky, '' = obsah příručky, jinak ID stránky
     */
    async load(doc, { refresh = false, push = true } = {}) {
        if (push && this.current) {
            this.history.push(this.current.doc);
        }

        const params = new URLSearchParams({
            url: window.location.pathname + window.location.search,
            tab: this.activeTab(),
        });
        if (doc === '') params.set('contents', '1');
        else if (doc !== null) params.set('doc', doc);
        if (refresh) params.set('refresh', '1');
        this.context = this.screenKey();

        this.body.innerHTML = '<div class="text-muted"><span class="spinner-border spinner-border-sm me-1"></span> Načítám nápovědu…</div>';

        let data;
        try {
            const url = this.toggle.dataset.helpPanelUrl + (this.toggle.dataset.helpPanelUrl.includes('?') ? '&' : '?') + params;
            const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } });
            data = await response.json();
        } catch (e) {
            data = { error: 'Nápovědu se nepodařilo načíst. Zkuste obnovit stránku.' };
        }

        this.render(data, doc);
    }

    render(data, requestedDoc) {
        this.current = { doc: data.doc?.id ?? (requestedDoc === '' || data.tree ? '' : requestedDoc), data };
        this.back.hidden = this.history.length === 0;
        this.wiki.hidden = !data.doc;
        if (data.doc) this.wiki.href = data.doc.url;

        if (data.error) {
            this.title.textContent = 'Nápověda';
            this.body.innerHTML = `<div class="alert alert-warning">${escapeHtml(data.error)}</div>`;
        } else if (data.doc) {
            this.title.textContent = data.doc.title;
            const stale = data.doc.stale ? '<div class="alert alert-secondary">Wiki teď neodpovídá, zobrazuji poslední staženou verzi.</div>' : '';
            this.body.innerHTML = stale + `<div class="help-content">${data.doc.html}</div>`;
        } else {
            this.title.textContent = 'Obsah příručky';
            const intro = requestedDoc === null ? '<p class="text-muted">K této obrazovce zatím není přiřazená stránka nápovědy. Vyberte téma:</p>' : '';
            this.body.innerHTML = intro + this.renderTree(data.tree ?? []);
        }
        this.body.scrollTop = 0;

        this.renderAssign(data);
    }

    renderTree(nodes) {
        if (!nodes.length) return '';
        return '<ul class="help-tree mb-1">' + nodes.map((node) => `
            <li><a href="#" data-help-doc="${escapeHtml(node.id)}">${escapeHtml(node.title)}</a>${this.renderTree(node.children ?? [])}</li>
        `).join('') + '</ul>';
    }

    /**
     * Jen pro správce: kterou stránku nápověda na této obrazovce otevírá a možnost nastavit
     * místo ní zobrazenou stránku. Sbalené do jednoho řádku, formulář až po "Změnit".
     */
    renderAssign(data) {
        const screens = data.screens ?? [];
        if (!data.doc || !screens.length) {
            this.assign.hidden = true;
            this.assign.innerHTML = '';
            return;
        }

        const tabLabel = document.querySelector('.app-body .nav-tabs .nav-link.active')?.textContent.replace(/\(\d+\)/, '').trim();
        const options = screens.map((s) => {
            const label = s.screen.includes('#') && tabLabel ? `jen záložku ${tabLabel}` : s.label;
            return `<option value="${escapeHtml(s.screen)}">${escapeHtml(label)}</option>`;
        }).join('');
        const scope = (screen) => (screen.includes('#') ? 'záložka' : (screen.split(':').length > 2 ? 'obrazovka' : 'celý modul'));
        const status = data.assignedScreen
            ? `Na této obrazovce se otevírá <strong>${escapeHtml(data.assignedTitle)}</strong> (${scope(data.assignedScreen)}).`
            : 'Na této obrazovce zatím není nastavená žádná stránka nápovědy.';

        this.assign.innerHTML = `
            <div class="d-flex align-items-center gap-2">
                <span class="text-muted flex-grow-1" data-help-assign-status>${status}</span>
                <a href="#" class="text-nowrap" data-help-assign-toggle>Změnit</a>
            </div>
            <form class="help-assign-form mt-2" hidden>
                <div class="text-muted mb-1">Otevírat zobrazenou stránku <strong>${escapeHtml(data.doc.title)}</strong> pro:</div>
                <div class="d-flex gap-2">
                    <select class="form-select form-select-sm" name="screen">${options}</select>
                    <button type="submit" class="btn btn-primary btn-sm text-nowrap" data-loading-indicator="false">Uložit</button>
                </div>
            </form>`;
        this.assign.querySelector('[data-help-assign-toggle]').addEventListener('click', (event) => {
            event.preventDefault();
            const form = this.assign.querySelector('form');
            form.hidden = !form.hidden;
        });
        this.assign.hidden = false;
    }

    handleLink(event) {
        const link = event.target.closest('a[data-help-doc]');
        if (!link) return;
        event.preventDefault();
        this.load(link.dataset.helpDoc);
    }

    async handleAssign(event) {
        event.preventDefault();
        const form = event.target;
        const button = form.querySelector('button');
        const body = new FormData();
        body.set('screen', form.elements.screen.value);
        body.set('doc', this.current.doc);
        button.disabled = true;

        try {
            const response = await fetch(this.toggle.dataset.helpAssignUrl, {
                method: 'POST',
                body,
                headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            });
            const data = await response.json();
            if (!data.ok) throw new Error(data.error);
            this.current.data.assignedScreen = data.assignedScreen;
            this.current.data.assignedTitle = data.assignedTitle;
            this.renderAssign(this.current.data);
            const status = this.assign.querySelector('[data-help-assign-status]');
            status.innerHTML = `<span class="text-success"><i class="bi bi-check-lg"></i></span> ${status.innerHTML}`;
        } catch (e) {
            button.disabled = false;
            this.assign.querySelector('[data-help-assign-status]').innerHTML = `<span class="text-danger">${escapeHtml(e.message || 'Uložení se nepovedlo.')}</span>`;
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const toggle = document.querySelector('.help-toggle');
    const panel = document.getElementById('help-panel');
    if (!toggle || !panel) return;

    const helpPanel = new HelpPanel(toggle, panel);
    naja.addEventListener('complete', () => helpPanel.screenChanged());
    if (wasOpen()) {
        helpPanel.open(false);
    }
});
